import { useEffect, useState } from 'react'
import { useHintergrundabruf } from './Abruf'
import { api } from './api'
import { Dialogfenster } from './Dialogfenster'
import { meldeErfolg } from './Erfolgsstelle'
import { fachlicherSatz } from './Fehlersatz'
import { Periodenfeld } from './Periodenfeld'
import { eingefrorene } from './Positionskonten'
import { useDarf } from './Rechte'
import { t } from './sprache'
import { periodenwort } from './Stammdaten'
import type { Abschluss } from './typen'
import { EINFUEGEN, ausgeschnitten, setzeErlaubnis, verwirf } from './Zwischenablage'
import type { Einfuegeauftrag } from './Zwischenablage'

/**
 * **Das Einfügen eines ausgeschnittenen Kontos** (7.3 Abschnitt 31, R1642,
 * R1643) — einmal in der Anwendung, für jedes Berichtsbild.
 *
 * Es fragt **ab Monat** — vorgeschlagen der des Berichts — **oder immer**,
 * wie das Positionsfenster (R860), und hängt das Konto über dieselbe Stelle
 * um. Was das Konto dabei erbt, entscheidet der Dienst (R1644); konnte es die
 * Kontengruppe nicht erben, sagt das Fenster es.
 *
 * **Esc verwirft das Ausgeschnittene**, solange kein Fenster offen ist.
 *
 * **Eine Position geht denselben Weg** (7.3 Abschnitt 32, R1697): dieselbe
 * Frage, und umgehängt über dieselbe Stelle wie im Gliederungsbaum — als
 * letzte Unterposition, mit Seite und Spiegel der neuen Mutter ab dem Monat
 * (R1698, R1699).
 */
export function Einfuegefenster({
  aufEingefuegt,
  periode,
}: {
  aufEingefuegt: () => void
  /** Der Monat des Berichts — vorgeschlagen, wo die Zelle keinen eigenen nennt. */
  periode: number | null
}) {
  const darf = useDarf()
  const darfUmhaengen = darf('kontoUmhaengen') && darf('positionUmhaengen')
  const [auftrag, setAuftrag] = useState<Einfuegeauftrag | null>(null)
  const [immer, setImmer] = useState(false)
  const [ab, setAb] = useState<number | null>(null)
  // Nur für den Hinweis, welche freigegebenen Abschlüsse ein „immer" erreicht
  // (R860) — fällt der Abruf aus, fehlt der Hinweis, das Einfügen nicht.
  const abschluesse = useHintergrundabruf<Abschluss[]>(
    auftrag !== null ? () => api.abschluesse() : null,
    [auftrag],
    [],
  )
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [fertig, setFertig] = useState<string[] | null>(null)

  useEffect(() => {
    setzeErlaubnis(darfUmhaengen)
  }, [darfUmhaengen])

  useEffect(() => {
    const ruf = (e: Event) => {
      const neu = (e as CustomEvent<Einfuegeauftrag>).detail
      setAuftrag(neu)
      setImmer(false)
      setAb(neu.periode ?? periode)
      setMeldung(null)
      setFertig(null)
    }
    window.addEventListener(EINFUEGEN, ruf)
    return () => window.removeEventListener(EINFUEGEN, ruf)
  }, [periode])

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && auftrag === null && ausgeschnitten() !== null) {
        verwirf()
      }
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [auftrag])

  if (auftrag === null) {
    return null
  }

  const gefroren = eingefrorene(abschluesse)

  async function einfuegen() {
    if (auftrag === null) {
      return
    }
    if (!immer && ab === null) {
      setMeldung(t('positionskonten.ohneperiode'))
      return
    }
    setLaeuft(true)
    setMeldung(null)
    try {
      const zug =
        auftrag.art === 'position'
          ? await api.positionUmhaengen(auftrag.schluessel, auftrag.position, null, ab ?? 0, immer, immer)
          : await api.kontoUmhaengen(auftrag.schluessel, auftrag.position, ab ?? 0, false, immer)
      verwirf()
      // Die Bestätigung geht über das Kästchen wie jede andere (R1130); was
      // nicht geerbt werden konnte, ist ein Hinweis und bleibt im Fenster.
      meldeErfolg(t(auftrag.art === 'position' ? 'erfolg.position.eingefuegt' : 'erfolg.konto.eingefuegt'))
      aufEingefuegt()
      if (zug.hinweis === 'gruppe-ohne-geschwister' || zug.hinweis === 'gruppe-uneins') {
        setFertig([t(`einfuegen.${zug.hinweis}`, { position: auftrag.positionsname })])
      } else {
        setAuftrag(null)
      }
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  const schliessen = () => setAuftrag(null)

  return (
    <Dialogfenster
      titel={t(auftrag.art === 'position' ? 'einfuegen.titel.position' : 'einfuegen.titel')}
      aufSchliessen={schliessen}
      fuss={
        <div className="aktionsreihe">
          {fertig === null && (
            <button type="button" disabled={laeuft} onClick={() => void einfuegen()}>
              {t('einfuegen.knopf')}
            </button>
          )}
          <button type="button" onClick={schliessen}>
            {t(fertig === null ? 'beleg.abbrechen' : 'einfuegen.schliessen')}
          </button>
        </div>
      }
    >
      {fertig !== null ? (
        fertig.map((satz) => <p key={satz}>{satz}</p>)
      ) : (
        <>
          <p>{t('einfuegen.satz', { konto: auftrag.name, position: auftrag.positionsname })}</p>
          <fieldset className="wirkungswahl">
            <legend>{t('einfuegen.wirkung')}</legend>
            <span className="wirkungab">
              <input
                type="radio"
                name="einfuegen-wirkung"
                aria-label={t('positionskonten.ab')}
                checked={!immer}
                onChange={() => setImmer(false)}
              />
              <Periodenfeld wert={ab} aufWert={setAb} gesperrt={immer} beschriftung={t('positionskonten.ab')} klasse="" />
            </span>
            <label>
              <input type="radio" name="einfuegen-wirkung" checked={immer} onChange={() => setImmer(true)} />
              {t('positionskonten.immer')}
            </label>
          </fieldset>
          {immer && gefroren.length > 0 && (
            <p className="hinweis">
              {t('positionskonten.freigegeben', { abschluesse: gefroren.map(periodenwort).join(', ') })}
            </p>
          )}
          {meldung && <p className="hinweis">{meldung}</p>}
        </>
      )}
    </Dialogfenster>
  )
}
